import { Route, Routes } from '@angular/router';
import { GlobalRouteGuard, routeUtils, SgPageNotFoundComponent } from '@sg/common';
import { SG_AUX_ROUTE_PATH, SgAuxRouteBridgeHostComponent } from '@sg/ui-kit/aux-route-bridge';

const createLazySgAppRouteData = routeUtils.createLazySgAppRouteData; // https://github.com/compodoc/compodoc/issues/525

export const ROUTES: Routes = [
  {
    path: SG_AUX_ROUTE_PATH,
    component: SgAuxRouteBridgeHostComponent,
    outlet: 'sgMainSidenav',
  },
  {
    path: '',
    canActivateChild: [GlobalRouteGuard],
    children: [
      {
        path: 'sg-admin',
        loadChildren: () => import('@sg/apps/admin').then((m) => m.SgAdminModule),
        data: createLazySgAppRouteData('sg-admin'),
      },
      {
        path: 'sg-emt',
        loadChildren: () => import('@sg/apps/new-admin').then((m) => m.SgNewAdminModule),
        data: createLazySgAppRouteData('sg-emt'),
      },
      {
        path: 'sg-analyzer',
        loadChildren: () => import('@sg/apps/analyzer').then((m) => m.SgAnalyzerModule),
        data: createLazySgAppRouteData('sg-analyzer'),
      },
      {
        path: 'sg-onboarding',
        loadChildren: () => import('@sg/apps/onboarding').then((m) => m.SgOnboardingModule),
        data: createLazySgAppRouteData('sg-onboarding'),
      },
      {
        path: 'sg-external',
        loadChildren: () => import('@sg/apps/external').then((m) => m.SgExternalModule),
        data: createLazySgAppRouteData('sg-external'),
      },
      {
        path: 'sg-borrower',
        loadChildren: () => import('@sg/apps/borrower').then((m) => m.SgBorrowerModule),
        data: createLazySgAppRouteData('sg-borrower'),
      },
      {
        path: 'sg-lender',
        loadChildren: () => import('@sg/apps/lender').then((m) => m.SgLenderModule),
        data: createLazySgAppRouteData('sg-lender'),
      },
      {
        path: 'sg-lead',
        loadChildren: () => import('@sg/apps/lead').then((m) => m.SgLeadModule),
        data: createLazySgAppRouteData('sg-lead'),
      },
      {
        path: 'sg-ui-kit',
        loadChildren: () => import('@sg/apps/ui-kit-examples').then((m) => m.UiKitExamplesDemoModule),
        data: createLazySgAppRouteData('sg-ui-kit'),
      },
    ],
  },
  { path: '**', component: SgPageNotFoundComponent },
];

/**
 * Returns a new `Routes` configuration without routes to lazy loaded modules that are unauthorized.
 * The `path` property for all lazy modules that are authorized is changed to an empty path ('').
 *
 * ## Changing paths to empty strings:
 * Changing all paths of authorized modules to empty string is done so the child application can control the application
 * as if it's the root module. If not set, the child module will not be able to handle URLs that does not start with the
 * path defined for each lazy loaded module.
 *
 * For example, App1 is a lazy loaded module defined with path `/app1`, if we don't change `/app1` to '' App1 can't*
 * control navigation's to `/` or `/something` etc...
 *
 * > App1 can control navigation's by listening to router events but that's not good.
 *
 * After we change `/app1` to `/` App1 has full control and it can create a redirect from `/` to `/app1`.
 * This means App1 must redeclare the path `/app1`.
 *
 * ## Why
 * Facts:
 *   1. Because the shell application is a host for other applications we need to make sure that only the right applications
 *      are loaded. Some applications can not co-exist with others so we need to regulate it.
 *   2. To compile an application the angular compiler must be aware of it, thus all lazy loaded applications must be
 *      declared in the root routes configuration.
 *
 * The facts above creates a conflict, we declare all in (2) even if some can't co exist.
 *
 * This function resolves this conflict by removing lazy loaded apps that are not provided in the `apps` arrays.
 * When the application loads it will automatically fetch the lazy module based on the url and then this function should
 * be called to remove all other lazy module paths so they can never load.
 *
 * This means this function must be called from the application root module constructor.
 *
 * Example:
 * We have 2 apps registers in `/app1` and `/app2`.
 * App1 & App2 can not co-exist, i.e. the can not live together in the same app.
 * The user browse to `/app1`, at this point the router will identify the route and fetch App1.
 * After the application loads, if the user browser to `/app2` the router will identify the route and fetch App2 while
 * App1 still runs, this is a conflict.
 *
 * If we called protectUnauthorizedRoutes('app1') from the constructor of the root module it will not happen because
 * when browsing to `/app2` the router will not find a URL that match's since we removed it.
 *
 * > Note #2: Using a `UrlMatcher` is a valid implementation for this scenario, however it does not play along with AOT.
 */
export function protectUnauthorizedRoutes(apps: string[]): Routes {
  const flatRoutes = [ROUTES].reduce((a, b) => a.concat(routeUtils.extractLazyRoutes(b)), []);
  const children: Route[] = [];

  for (const app of apps) {
    const route = flatRoutes.find((r) => r.data && r.data.sgAppName && r.data.sgAppName === app && !!r.loadChildren);
    if (route) {
      route.path = '';
      children.push(route);
    }
  }

  ROUTES[1].children = children;

  return ROUTES;
}
